import type { LoopProjection } from "@/lib/loop/math";
import { formatHealthFactor, type RiskMetrics } from "@/lib/loop/risk";
import { formatNumber } from "@/lib/format";
import { CADC } from "@/config/tokens";

type Props = {
  readonly projection: LoopProjection;
  readonly principal: number;
  /** Risk after the loop is applied on top of any existing position. */
  readonly projectedRisk: RiskMetrics | null;
};

type Tone = "ink" | "jade" | "rose";

type Row = {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: Tone;
};

const hfTone = (hf: number | undefined): Tone => {
  if (hf == null || !Number.isFinite(hf)) return "ink";
  if (hf < 1.1) return "rose";
  if (hf < 1.5) return "ink";
  return "jade";
};

const rowsFor = (
  p: LoopProjection,
  principal: number,
  projectedRisk: RiskMetrics | null,
): Row[] => [
  {
    label: "Net principal",
    value: `${formatNumber(principal, 2)} ${CADC.symbol}`,
  },
  {
    label: "Total collateral",
    value: `${formatNumber(p.totalCollateral, 2)} ${CADC.symbol}`,
    hint: `${formatNumber(p.effectiveLeverage, 2)}× exposure`,
    tone: "jade",
  },
  {
    label: "Borrowed against",
    value: `${formatNumber(p.totalBorrow, 2)} USDC`,
    hint: `${p.iterations} loop ${p.iterations === 1 ? "step" : "steps"}`,
  },
  {
    label: "Health factor",
    value: projectedRisk ? formatHealthFactor(projectedRisk.healthFactor) : "—",
    hint: "post-loop · liquidates < 1.00",
    tone: hfTone(projectedRisk?.healthFactor),
  },
  {
    label: "Liquidation price",
    value: projectedRisk?.liquidationPrice != null
      ? `${formatNumber(projectedRisk.liquidationPrice, 4)} USDC`
      : "—",
    hint: `cap ${formatNumber(p.maxLeverage, 2)}×`,
    tone: projectedRisk?.liquidationPrice != null && projectedRisk.liquidationPrice > 0.99 ? "rose" : "ink",
  },
  {
    label: "Est. swap cost",
    value: `${formatNumber(p.estSlippageCost, 2)} USDC`,
    hint: "across all iterations",
  },
];

const toneColor = (tone?: Row["tone"]): string => {
  if (tone === "jade") return "var(--accent-jade)";
  if (tone === "rose") return "var(--accent-rose)";
  return "var(--ink-1)";
};

export const LoopSummary = ({ projection, principal, projectedRisk }: Props) => {
  const rows = rowsFor(projection, principal, projectedRisk);
  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Projected position</h3>
      </div>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span className="text-right">
              <span
                className="font-mono tabular text-[14px]"
                style={{ color: toneColor(r.tone), fontWeight: 400 }}
              >
                {r.value}
              </span>
              {r.hint && (
                <span
                  className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
                  style={{ color: "var(--ink-4)" }}
                >
                  {r.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
